import {useTranslation} from '../../utils/useTranslation'
import {theme} from '../../theme'
import {useViewMode, useUIActions, useListDateMode} from '../../store'
import {useFilterTracking} from '../../hooks/filterTracking.hooks'
import ButtonGroup from '../buttonGoup'
import IconButton from '../iconButton'

interface ViewModeSwitchProps {
  /**
   * Adds a third "period" (date-range) segment. Only the list tabs
   * (activities, notices) pass this — other screens keep the plain
   * week/day switch and never see it.
   */
  showPeriod?: boolean
}

/**
 * Week / day (/ period) view-mode switch. Self-contained: reads and writes
 * `viewMode` from the UI store and tracks its own usage. Rendered as a
 * ButtonGroup segmented pill so it shares the same visual language as the other
 * filter switches.
 *
 * The period segment toggles the list-local `listDateMode` — it does NOT
 * touch the shared `viewMode`, so the last week/day mode is preserved underneath.
 * Selecting week/day clears period mode back to the shared cursor.
 */
export default function ViewModeSwitch({
  showPeriod = false,
}: ViewModeSwitchProps) {
  const {t} = useTranslation()
  const viewMode = useViewMode()
  const dateMode = useListDateMode()
  const {setViewMode, setListDateMode} = useUIActions()
  const trackFilter = useFilterTracking()

  // On non-period switches, period is never active. On the list, the selected
  // segment is period when the list is in period mode, otherwise the viewMode.
  const isPeriod = showPeriod && dateMode === 'period'

  const colorFor = (active: boolean) =>
    active ? theme.palette.primary.main : theme.palette.text.primary

  const selectCursor = (mode: 'week' | 'day') => {
    setViewMode(mode)
    // Leaving period is an explicit act: return the list to the shared cursor.
    if (showPeriod) setListDateMode('cursor')
    trackFilter('viewMode')
  }

  const selectPeriod = () => {
    setListDateMode('period')
    // Distinct control name: period adoption must be separable from the
    // ordinary week/day toggling.
    trackFilter('periodView')
  }

  return (
    <ButtonGroup>
      {showPeriod && (
        <IconButton
          icon='calendarPeriod'
          iconSize='24px'
          iconColor={colorFor(isPeriod)}
          active={isPeriod}
          onClick={selectPeriod}
          tooltip={t('tooltip:periodView')}
        />
      )}
      <IconButton
        icon='calendarWeek'
        iconSize='24px'
        iconColor={colorFor(!isPeriod && viewMode === 'week')}
        active={!isPeriod && viewMode === 'week'}
        onClick={() => selectCursor('week')}
        tooltip={t('tooltip:weekView')}
      />
      <IconButton
        icon='hours24'
        iconSize='24px'
        iconColor={colorFor(!isPeriod && viewMode === 'day')}
        active={!isPeriod && viewMode === 'day'}
        onClick={() => selectCursor('day')}
        tooltip={t('tooltip:dayView')}
      />
    </ButtonGroup>
  )
}
